<script lang="ts">
  import WorkoutPlayerCard from './WorkoutPlayerCard.svelte';
  import { cn } from '@/lib/utils';
  import { type WorkoutPlayerType } from '@/Util/api';
  import ChooseTrainingSheet from './ChooseTrainingSheet.svelte';
  import { allWorkoutPlayers, workoutPlayerTypeToName } from '@/Common/constants';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import { recommendedTraining } from '@/Singletons/RecommendedTrainingWritable';
  import { DataStatus } from '@/Singletons/common';
  import { Button } from '@/lib/components/ui/button';
  import { connectedWorkoutPlayers } from '@/Singletons/WorkoutPlayersWritable';
  import * as Dialog from '$lib/components/ui/dialog';
  import WorkoutPlayerConnectionManager from './WorkoutPlayerConnectionManager.svelte';

  let containerSize: ResizeObserverSize[] | null = null;
  let containerWidth: number | null = null;
  $: if (containerSize && containerSize[0]) containerWidth = containerSize[0].inlineSize;

  let chooseTrainingSheetOpen = false;
  let chooseTrainingSheetWorkoutPlayer: WorkoutPlayerType = 'garmin_connect';

  let workoutPlayerConnectionManagerDialogOpen = false;

  function handleChooseNewTraining(source: WorkoutPlayerType) {
    chooseTrainingSheetWorkoutPlayer = source;
    chooseTrainingSheetOpen = true;
  }

</script>

<div class="space-y-4">
  <!-- Header actions -->
  <div class="flex flex-wrap items-center gap-2">
    <div class="text-sm text-gray-600">View and manage workouts sent to your connected apps and devices.</div>
    <div class="flex-1" />
    <Button variant='outline' size='sm' on:click={() => connectedWorkoutPlayers.refresh()}>Refresh</Button>
    <Button variant='outline' size='sm' on:click={() => workoutPlayerConnectionManagerDialogOpen = true}>
      <i class='fa-light fa-lg fa-mp3-player text-xs mr-1' />Manage Workout Players
    </Button>
  </div>

  {#if $connectedWorkoutPlayers.status !== DataStatus.READY && $connectedWorkoutPlayers.status !== DataStatus.RESYNCING}
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {#each Array.from({ length: 3 }) as _}
        <Skeleton class="h-40" />
      {/each}
    </div>
  {:else if $connectedWorkoutPlayers.players.length === 0}
    <div class="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
      <div class="mb-2 text-base font-medium">No workout players connected</div>
      <div class="mb-4 text-sm text-gray-600">Connect a workout player to start sending workouts.</div>
      <div class="flex flex-wrap justify-center gap-2">
        {#each allWorkoutPlayers as wp}
          <Button on:click={() => connectedWorkoutPlayers.addWorkoutPlayer(wp)}>
            Connect {workoutPlayerTypeToName[wp]}
          </Button>
        {/each}
      </div>
    </div>
  {:else}
    <div bind:borderBoxSize={containerSize} class={cn('grid grid-cols-1 gap-3', containerWidth >= 632 ? 'grid-cols-2' : '', containerWidth >= 954 ? 'grid-cols-3' : '')}>
      {#if containerWidth >= 954}
        {@const players1 = $connectedWorkoutPlayers.players?.filter((_, i) => i % 3 === 0)}
        {@const players2 = $connectedWorkoutPlayers.players?.filter((_, i) => i % 3 === 1)}
        {@const players3 = $connectedWorkoutPlayers.players?.filter((_, i) => i % 3 === 2)}
        <div class='flex flex-col gap-3'>
          {#each players1 as wp}
            <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
          {/each}
        </div>
        <div class='flex flex-col gap-3'>
          {#each players2 as wp}
            <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
          {/each}
        </div>
        <div class='flex flex-col gap-3'>
          {#each players3 as wp}
            <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
          {/each}
        </div>
      {:else if containerWidth >= 632}
        {@const playersEven = $connectedWorkoutPlayers.players?.filter((_, i) => i % 2 === 0)}
        {@const playersOdd = $connectedWorkoutPlayers.players?.filter((_, i) => i % 2 === 1)}
        <div class='flex flex-col gap-3'>
          {#each playersEven as wp}
            <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
          {/each}
        </div>
        <div class='flex flex-col gap-3'>
          {#each playersOdd as wp}
            <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
          {/each}
        </div>
      {:else}
        {#each $connectedWorkoutPlayers.players as wp}
          <WorkoutPlayerCard workoutPlayer={wp} onSendNew={handleChooseNewTraining} />
        {/each}
      {/if}
    </div>
  {/if}
</div>

<Dialog.Root bind:open={workoutPlayerConnectionManagerDialogOpen} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-col max-h-[95dvh] w-[300px] rounded-xl">
    <Dialog.Header class="border-b border-border pb-1">
      <Dialog.Title>
        Add or Remove Workout Players
      </Dialog.Title>
    </Dialog.Header>

    <WorkoutPlayerConnectionManager />
    
    <Dialog.Footer class="border-t border-border pt-2">
      <Button variant="outline" on:click={() => {
        workoutPlayerConnectionManagerDialogOpen = false;
      }}>
        Close
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>

<ChooseTrainingSheet
  bind:open={chooseTrainingSheetOpen}
  isLoading={$recommendedTraining.status === DataStatus.LOADING || $recommendedTraining.status === DataStatus.RESYNCING}
  onConfirm={(exercise) => {
    connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer(chooseTrainingSheetWorkoutPlayer, exercise.workout);
  }}
  sheetTitle="Choose a Workout to Send to {workoutPlayerTypeToName[chooseTrainingSheetWorkoutPlayer].length > 15 ? workoutPlayerTypeToName[chooseTrainingSheetWorkoutPlayer].slice(0, 15) + '...' : workoutPlayerTypeToName[chooseTrainingSheetWorkoutPlayer]}"
  actionButtonText={`Send to ${workoutPlayerTypeToName[chooseTrainingSheetWorkoutPlayer]}`}
  confirmButtonText={(exercise) => {
    const name = exercise?.name?.length > 15 ? exercise.name.slice(0, 15) + '...' : exercise?.name;
    const device = workoutPlayerTypeToName[chooseTrainingSheetWorkoutPlayer];
    const deviceLabel = device.length > 15 ? device.slice(0, 15) + '...' : device;
    return `Send ${name ?? 'workout'} to ${deviceLabel}`;
  }}
  originalExercises={$recommendedTraining.exercises?.filter((exercise) => !!exercise?.workout?.path)}
  noExercisesFoundAdditionalInfo="Note: This search only includes workouts, not activities or sessions."
/>
